import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, Copy } from 'lucide-react';
import { tasksApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useProjectRoles } from '@/hooks/useProjectRoles';

interface DuplicateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: {
    id: string;
    title: string;
    status: string;
    dueDate?: string;
    startDate?: string;
    assigneeId?: string;
  };
  projectId: string;
  currentListId: string;
  taskLists: Array<{ id: string; name: string }>;
}

type CopyMode = 'everything' | 'customize';

export function DuplicateTaskModal({
  isOpen,
  onClose,
  task,
  projectId,
  currentListId,
  taskLists,
}: DuplicateTaskModalProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(`${task.title} (copy)`);
  const [selectedListId, setSelectedListId] = useState(currentListId);
  const [copyMode, setCopyMode] = useState<CopyMode>('everything');
  // Staff on THIS project: Solid anywhere, a partner agency on its own
  const { isProjectStaff: isAgencyMember } = useProjectRoles(projectId);
  const [copyAttachments, setCopyAttachments] = useState(true);
  const [copyDueDate, setCopyDueDate] = useState(true);
  const [copyStartDate, setCopyStartDate] = useState(true);
  const [copyAssignee, setCopyAssignee] = useState(true);
  const [copyComments, setCopyComments] = useState(true);
  const [copyCommentAttachments, setCopyCommentAttachments] = useState(true);
  const [keepStatus, setKeepStatus] = useState(false);
  const [sendNotifications, setSendNotifications] = useState(false);

  const duplicateMutation = useMutation({
    mutationFn: () =>
      tasksApi.duplicateTask(task.id, {
        title,
        listId: selectedListId,
        copyEverything: copyMode === 'everything',
        copyAttachments: copyMode === 'customize' ? copyAttachments : undefined,
        copyDueDate: copyMode === 'customize' ? copyDueDate : undefined,
        copyStartDate: copyMode === 'customize' ? copyStartDate : undefined,
        copyAssignee: copyMode === 'customize' ? copyAssignee : undefined,
        copyComments: copyMode === 'customize' ? copyComments : undefined,
        copyCommentAttachments: copyMode === 'customize' ? copyCommentAttachments : undefined,
        keepStatus: copyMode === 'customize' ? keepStatus : undefined,
        sendNotifications,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', selectedListId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
      onClose();
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-xl shadow-elevated w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-grey-100">
          <div className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-accent" />
            <h2 className="text-heading-4 text-secondary">Duplicate Task</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-grey-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-grey-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Task Name */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">
              New Task Name
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="input w-full"
              autoFocus
            />
          </div>

          {/* List Selector */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-1">
              Target List
            </label>
            <select
              value={selectedListId}
              onChange={(e) => setSelectedListId(e.target.value)}
              className="input w-full"
            >
              {taskLists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
          </div>

          {/* Copy Mode Tabs */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              What to Copy
            </label>
            <div className="flex rounded-lg border border-grey-200 overflow-hidden">
              <button
                onClick={() => setCopyMode('everything')}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                  copyMode === 'everything'
                    ? 'bg-secondary text-white'
                    : 'bg-white text-grey-600 hover:bg-grey-50'
                }`}
              >
                Everything
              </button>
              <button
                onClick={() => setCopyMode('customize')}
                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                  copyMode === 'customize'
                    ? 'bg-secondary text-white'
                    : 'bg-white text-grey-600 hover:bg-grey-50'
                }`}
              >
                Customize
              </button>
            </div>
          </div>

          {/* Customize Checkboxes */}
          {copyMode === 'customize' && (
            <div className="space-y-2.5 pl-1">
              <CheckboxOption
                label="Attachments"
                checked={copyAttachments}
                onChange={setCopyAttachments}
              />
              <CheckboxOption
                label="Due Date"
                checked={copyDueDate}
                onChange={setCopyDueDate}
              />
              {task.startDate && isAgencyMember && (
                <CheckboxOption
                  label="Start Date"
                  checked={copyStartDate}
                  onChange={setCopyStartDate}
                />
              )}
              <CheckboxOption
                label="Assignee"
                checked={copyAssignee}
                onChange={setCopyAssignee}
              />
              <CheckboxOption
                label="Comments"
                checked={copyComments}
                onChange={setCopyComments}
              />
              {copyComments && (
                <CheckboxOption
                  label="Comment Attachments"
                  checked={copyCommentAttachments}
                  onChange={setCopyCommentAttachments}
                  className="ml-4"
                />
              )}
              <CheckboxOption
                label="Keep Task Status"
                checked={keepStatus}
                onChange={setKeepStatus}
              />
            </div>
          )}

          {/* Send Notifications Toggle */}
          <CheckboxOption
            label="Send notifications"
            checked={sendNotifications}
            onChange={setSendNotifications}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-grey-100">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => duplicateMutation.mutate()}
            disabled={!title.trim() || duplicateMutation.isPending}
            isLoading={duplicateMutation.isPending}
          >
            {duplicateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            Duplicate
          </Button>
        </div>
      </div>
    </div>
  );
}

function CheckboxOption({
  label,
  checked,
  onChange,
  className,
}: {
  label: string;
  checked: boolean;
  onChange: (val: boolean) => void;
  className?: string;
}) {
  return (
    <label className={`flex items-center gap-2 cursor-pointer ${className || ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-grey-300 text-accent focus:ring-accent"
      />
      <span className="text-sm text-secondary">{label}</span>
    </label>
  );
}
